import React from 'react';
import { Link } from 'react-router-dom';
import ProjectVideo from './ProjectVideo';
import { featuredProjects, MAX_PROJECT_PHOTOS } from '../../data/projects';

// Featured projects section. Content is admin-managed: the projects marked
// "featured" in the `projects` site setting (Admin -> Storefront -> Content),
// read through data/projects.js, which also feeds the /gallery page.
//
// A project appears here only when it has at least two photos. The layout
// adapts to how many photos there are (2 = a pair of tiles, 3 = the
// large-plus-two collage). If no project qualifies, the whole section renders
// nothing rather than showing empty tiles. The project's first video shows
// under its photos.
//
// Each photo carries `focus`, the vertical focal point as a percentage: phone
// photos are portrait and get cropped hard into the mobile tile, and the right
// point differs per shot - a wall-mounted cassette sits high, an extended
// awning sits lower - so one site-wide value can't serve them all.

// In the 3-photo collage the first photo is the large tile and the other two
// stack beside it. With two photos they share the row as equal tiles, so no
// per-tile class is needed (the grid modifier handles it).
function tileClass(count, i) {
  if (count >= 3) return ['asg-featured-main', 'asg-featured-top', 'asg-featured-bottom'][i] || '';
  return '';
}
function tileDims(count, i) {
  if (count >= 3 && i === 0) return { width: 1200, height: 1200 };
  return { width: 800, height: 600 };
}

export default function FeaturedProject({ settings = {} }) {
  const ready = featuredProjects(settings);

  if (ready.length === 0) return null;

  return (
    <section className="asg-featured" id="projects">
      <div className="asg-featured-intro asg-reveal">
        <p className="asg-section-label">
          {ready.length > 1 ? 'Featured Projects' : 'Featured Project'}
        </p>
      </div>

      {ready.map((project) => {
        const photos = project.photos.slice(0, MAX_PROJECT_PHOTOS);
        const n = photos.length;
        // Poster falls back to the first photo, so an mp4 alone is enough to
        // show the video (a dedicated poster upload is optional).
        const video = project.videos[0];
        const poster = (video && video.poster) || (photos[0] && photos[0].url) || '';
        const hasVideo = !!(video && poster);
        return (
          <article className="asg-featured-item asg-reveal" key={project.id}>
            <div className="asg-featured-header">
              <div>
                <h2 className="asg-section-title">{project.title}</h2>
              </div>
            </div>

            <div className={`asg-featured-grid asg-featured-grid--${n}`}>
              {photos.map((photo, i) => {
                const cls = tileClass(n, i);
                const dims = tileDims(n, i);
                return (
                  <div className={`asg-featured-img${cls ? ' ' + cls : ''}`} key={i}>
                    <img
                      src={photo.url}
                      alt={`${project.title} - photo ${i + 1}`}
                      width={dims.width}
                      height={dims.height}
                      loading="lazy"
                      // Consumed by the mobile crop rule in HomePage.css.
                      style={{ '--asg-img-focus': `${photo.focus}%` }}
                    />
                  </div>
                );
              })}
            </div>

            {hasVideo && (
              <ProjectVideo
                video={{
                  mp4: video.mp4,
                  webm: video.webm,
                  poster,
                  alt: `${project.title} - project video`,
                }}
                title={project.title}
              />
            )}

            <div className="asg-featured-caption">
              <p className="asg-featured-caption-text">
                <strong>{project.caption}</strong>
              </p>
              {project.location && (
                <p className="asg-featured-caption-text asg-featured-caption-text--location">
                  {project.location}
                </p>
              )}
            </div>
          </article>
        );
      })}

      <div className="asg-featured-more">
        <Link to="/gallery" className="asg-btn-secondary">See all our projects</Link>
      </div>
    </section>
  );
}
